import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { applyMasksToRegistro, formatNumber } from 'utils/formatUtils';
import { DadosCamposPorRegistro } from 'utils/types/blocoRegistros';
import { CamposNaoVisiveisListagem, TipoCampo } from 'utils/types/campo';

import { DeleteOutline, EditNote, Visibility } from '@mui/icons-material';
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material';

import AlertModal from 'components/AlertModal';

import { useDeleteRegistro } from './dynamicDataTable.service';
import {
  ActionsContainer,
  StyledTableContainer,
} from './DynamicDataTable.styles';

interface DynamicDataTableProps {
  processamentoSpedId: string;
  dadosCamposPorRegistro: DadosCamposPorRegistro;
  onEditRegistro: (registroIndex: number) => void;
  onDeleteRegistro: (registroId: number) => void;
  registroCodigo: string;
  registroId?: number;
  possuiRegistroFilho: boolean;
  onVisualizarRegistrosFilhos?: (
    registroId: number,
    registroLoaderId: number,
  ) => void;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  modo: 'add' | 'edit';
  isModalAberto: boolean;
  setIsModalAberto: (open: boolean) => void;
  registroSelecionadoIndex?: number | null;
  refetchCamposPorRegistro: () => void;
}

const DynamicDataTable: React.FC<DynamicDataTableProps> = ({
  processamentoSpedId,
  dadosCamposPorRegistro,
  registroCodigo,
  onEditRegistro,
  onDeleteRegistro,
  registroId,
  possuiRegistroFilho,
  onVisualizarRegistrosFilhos,
  onPageChange,
  onRowsPerPageChange,
  refetchCamposPorRegistro,
}) => {
  const dadosComMascaras = applyMasksToRegistro(dadosCamposPorRegistro);

  // desestrutura normalmente
  const { campos, valores } = dadosComMascaras;
  const [linhaFilhosCarregadosId, setLinhaFilhosCarregadosId] = useState<
    number | null
  >(null);
  const [modalOpen, setOpenModal] = useState<boolean>(false);
  const [registroIdParaExcluir, setRegistroIdParaExcluir] = useState<
    number | null
  >(null);
  const [linhaParaExcluir, setLinhaParaExcluir] = useState<number | null>(null);

  const queryClient = useQueryClient();

  const handleChangePage = (_event: unknown, newPage: number) => {
    onPageChange?.(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    onRowsPerPageChange?.(newRowsPerPage);
    onPageChange?.(1);
  };

  const handleExclusionModal = () => setOpenModal(!modalOpen);

  const handleOpenExclusionModal = (
    registroId: number,
    numeroLinha: number,
  ) => {
    setRegistroIdParaExcluir(registroId);
    setLinhaParaExcluir(numeroLinha);
    setOpenModal(true);
  };

  const { mutate: excluirRegistro, isPending: isExcluindoRegistro } =
    useDeleteRegistro(processamentoSpedId, registroCodigo, queryClient);

  const handleDeleteRegistro = () => {
    if (registroIdParaExcluir == null) return;
    excluirRegistro(registroIdParaExcluir, {
      onSuccess: () => {
        toast.success('Registro excluído com sucesso!');
        refetchCamposPorRegistro();
        onDeleteRegistro(registroIdParaExcluir);
        setOpenModal(false);
      },
      onError: () => {
        toast.error('Não foi possível excluir o registro.');
      },
    });
  };

  const handleVisualizarRegistrosFilhos = (loaderId: number) => {
    if (registroId !== undefined) {
      setLinhaFilhosCarregadosId(loaderId);
      onVisualizarRegistrosFilhos?.(registroId, loaderId);
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  };

  return (
    <>
      <AlertModal
        open={modalOpen}
        onClose={handleExclusionModal}
        confirmButtonAction={handleDeleteRegistro}
        title="Atenção"
        content={`Deseja realmente remover?<br><br>
            <strong> Registro: ${registroCodigo} </strong><br>
            <strong> Linha: ${linhaParaExcluir || '-'} </strong><br><br>
          Essa ação também irá remover todos os registros filhos.`}
        cancelButtonText="Não"
        confirmButtonText={isExcluindoRegistro ? 'Excluindo...' : 'Sim'}
        confirmButtonDisabled={isExcluindoRegistro}
      />

      <StyledTableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Ações</TableCell>
              {campos
                .filter(
                  (campo) =>
                    !CamposNaoVisiveisListagem.includes(campo.referencia),
                )
                .map((campo) => (
                  <TableCell key={campo.referencia}>
                    {campo.descricao}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {valores.conteudo.map((registro, index) => (
              <TableRow
                key={index}
                sx={
                  Number(registro?.ID) === linhaFilhosCarregadosId
                    ? { backgroundColor: '#e1f3fa' }
                    : {}
                }
              >
                <TableCell align="center">
                  <ActionsContainer>
                    {possuiRegistroFilho && (
                      <Tooltip title="Visualizar registros filhos">
                        <IconButton
                          data-testid="visualizar-registros-filhos-button"
                          size="small"
                          color="secondary"
                          onClick={() => {
                            handleVisualizarRegistrosFilhos(
                              Number(registro?.ID),
                            );
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Editar registro">
                      <IconButton
                        data-testid="edit-registro-button"
                        size="small"
                        color="primary"
                        onClick={() => onEditRegistro(index)}
                      >
                        <EditNote />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir registro">
                      <IconButton
                        data-testid="delete-registro-button"
                        size="small"
                        color="error"
                        onClick={() =>
                          handleOpenExclusionModal(
                            Number(registro.ID),
                            Number(registro.LINHA),
                          )
                        }
                      >
                        <DeleteOutline />
                      </IconButton>
                    </Tooltip>
                  </ActionsContainer>
                </TableCell>
                {campos
                  .filter(
                    (campo) =>
                      !CamposNaoVisiveisListagem.includes(campo.referencia),
                  )
                  .map((campo) => (
                    <TableCell
                      key={campo.referencia}
                      sx={
                        (registro[campo.referencia] || '').length <= 20
                          ? { whiteSpace: 'nowrap' }
                          : undefined
                      }
                    >
                      {(() => {
                        const raw: unknown = registro[campo.referencia];

                        if (raw === null || raw === undefined || raw === '')
                          return '-';

                        if (
                          campo.tipoCampo === TipoCampo.number &&
                          campo.mascara === null
                        ) {
                          if (typeof raw === 'number') {
                            return campo.decimal != null
                              ? raw.toFixed(campo.decimal).replace('.', ',')
                              : String(raw);
                          }

                          if (typeof raw === 'string') {
                            const hasDecimalSeparator = raw.includes(',') || raw.includes('.');
                            const cleaned = raw.replace(/[^0-9.,-]/g, '');

                            if (!hasDecimalSeparator) 
                              return cleaned;               

                            const normalized = cleaned.replace(/\./g, '').replace(',', '.');
                            const parsed = Number(normalized);

                            if (!isNaN(parsed)) {
                              return campo.decimal != null
                                ? parsed.toFixed(campo.decimal).replace('.', ',')
                                : String(parsed);
                            }
                          }

                          return String(raw);
                        }

                        return String(raw);
                      })()}
                    </TableCell>
                  ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={valores.totalConteudo}
        rowsPerPage={valores.quantidadeNaPagina}
        page={valores.pagina - 1}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Itens por página"
        labelDisplayedRows={({ from, to, count }) =>
          `${from} - ${to} de ${count !== -1 ? count : `mais de ${to}`}`
        }
      />
    </>
  );
};

export default DynamicDataTable;
